import React, { useState, useEffect, useCallback, memo } from 'react';
import { View, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity, Alert } from 'react-native';
import { Text, TextInput, Surface, IconButton, useTheme, Portal, Snackbar, Dialog, Button } from 'react-native-paper';
import { whatsappService } from '../services/whatsappService';
import { useNavigation } from '@react-navigation/native';
import { formatDate, formatCurrency } from '../utils/format';
import type { DrawerNavigationProp } from '@react-navigation/drawer';
import { Cliente } from '../types/Cliente';
import { useListaClientes } from '../hooks/useListaClientes';
import { supabase } from '../services/supabase';

type RootStackParamList = {
  DrawerHome: undefined;
  ListaTitulos: { cliente: Cliente };
};

type NavigationProp = DrawerNavigationProp<RootStackParamList>;

interface UserData {
  empresa: number; // ID da empresa, ex: 1
  codusur: string; // Código do usuário, ex: "2404"
  empresaRota: string; // Rota da empresa para API, ex: "UNIFRIOS"
  [key: string]: any; // Para outras propriedades que possam vir
}

// A função formatDate foi removida para evitar duplicação
// Agora estamos usando a função importada de utils/format.ts

// Função para validar data
const isValidDate = (date: string) => {
  if (date.length !== 10) return false;
  
  const [day, month, year] = date.split('/').map(Number);
  const dateObj = new Date(year, month - 1, day);
  
  return dateObj instanceof Date && !isNaN(dateObj.getTime()) &&
    dateObj.getDate() === day &&
    dateObj.getMonth() === month - 1 &&
    dateObj.getFullYear() === year;
};

// Função para converter data para string DD/MM/YYYY
const dateToString = (date: Date | null) => {
  if (!date) return '';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

// Função para converter string DD/MM/YYYY para Date
const stringToDate = (dateStr: string) => {
  if (!dateStr || !isValidDate(dateStr)) return null;
  const [day, month, year] = dateStr.split('/').map(Number);
  return new Date(year, month - 1, day);
};

// Função para calcular dias de atraso
const calcularDiasAtraso = (dataVencimento: string) => {
  if (!dataVencimento) return 0;
  const hoje = new Date();
  const vencimento = new Date(dataVencimento);
  const diff = Math.floor((hoje.getTime() - vencimento.getTime()) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
};

// Função para obter cor de status baseado nos dias de atraso
const getStatusColor = (diasAtraso: number) => {
  if (diasAtraso === 0) return '#4CAF50'; // Verde - em dia
  if (diasAtraso <= 30) return '#FF9800'; // Laranja - até 30 dias
  return '#F44336'; // Vermelho - mais de 30 dias
};

// Componente de card memoizado para evitar re-renders desnecessários
const ClienteCard = memo(({ cliente, formatCurrency, onPress, onRobotPress }: { 
  cliente: Cliente; 
  formatCurrency: (value: string) => string; 
  onPress: () => void;
  onRobotPress: (cliente: Cliente) => void;
}) => {
  const diasAtraso = calcularDiasAtraso(cliente.D_MINDTVENC || '');
  const statusColor = getStatusColor(diasAtraso);
  
  return (
    <Surface style={[styles.card, { borderLeftColor: statusColor }]} elevation={2}>
      <TouchableOpacity onPress={onPress} style={styles.cardContent}>
        <View style={styles.cardHeader}>
          <View style={styles.cardHeaderLeft}>
            <View style={[styles.statusBadge, { backgroundColor: statusColor }]}>
              <Text style={styles.statusBadgeText}>{diasAtraso > 0 ? `${diasAtraso}d` : 'OK'}</Text>
            </View>
            <View>
              <Text style={styles.clientCode}>#{cliente.I_CODCLI}</Text>
              <Text style={styles.clientName} numberOfLines={2}>{cliente.S_CLIENTE}</Text>
            </View>
          </View>
        </View>
        
        <View style={styles.cardInfo}>
          <View style={styles.infoItem}>
            <IconButton icon="file-document-multiple" size={16} style={styles.infoIcon} />
            <View>
              <Text style={styles.infoLabel}>Títulos</Text>
              <Text style={styles.infoValue}>{cliente.I_QTDETITULOS}</Text>
            </View>
          </View>
          <View style={styles.infoItem}>
            <IconButton icon="cash" size={16} style={styles.infoIcon} />
            <View>
              <Text style={styles.infoLabel}>Valor</Text>
              <Text style={[styles.infoValue, styles.infoValueMoney]}>{formatCurrency(cliente.F_VALORDEB || '0')}</Text>
            </View>
          </View>
          <View style={styles.infoItem}>
            <IconButton icon="message-alert" size={16} style={styles.infoIcon} />
            <View>
              <Text style={styles.infoLabel}>Cobranças</Text>
              <Text style={styles.infoValue}>{cliente.I_COBRANCAS}</Text>
            </View>
          </View>
        </View>
      </TouchableOpacity>
      
      <IconButton
        icon="whatsapp"
        size={28}
        iconColor="#25D366"
        onPress={() => onRobotPress(cliente)}
        style={styles.robotButton}
      />
    </Surface>
  );
});

export function Cobrancas() {
  const theme = useTheme();
  const navigation = useNavigation<NavigationProp>();
  const [showFilters, setShowFilters] = useState(false);
  const [codCliente, setCodCliente] = useState('');
  const [razaoSocial, setRazaoSocial] = useState('');
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loadingSync, setLoadingSync] = useState(false);
  const [enviandoMensagem, setEnviandoMensagem] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState<Cliente | null>(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [telefoneEnvio, setTelefoneEnvio] = useState('5582988715493');
  const [ddiOpcao4, setDdiOpcao4] = useState('55');
  const [titulosCliente, setTitulosCliente] = useState<any[]>([]);
  const [carregandoTitulos, setCarregandoTitulos] = useState(false);
  
  // Estado para o Snackbar
  const [snackbar, setSnackbar] = useState({
    visible: false,
    mensagem: '',
    tipo: 'info' as 'info' | 'success' | 'error'
  });
  
  // Define data inicial como primeiro dia do ano anterior
  const hoje = new Date();
  const primeiroDoAnoAnterior = new Date(hoje.getFullYear() - 1, 0, 1);
  const dataInicialPadrao = dateToString(primeiroDoAnoAnterior);
  
  const [dataInicial, setDataInicial] = useState(dataInicialPadrao);
  const [dataFinal, setDataFinal] = useState('');
  
  // Estados para controlar o debounce
  const [debouncedCodCliente, setDebouncedCodCliente] = useState('');
  const [debouncedRazaoSocial, setDebouncedRazaoSocial] = useState('');
  const [debouncedDataInicial, setDebouncedDataInicial] = useState(dataInicialPadrao);
  const [debouncedDataFinal, setDebouncedDataFinal] = useState('');

  // Aumenta o delay do debounce para 1.5 segundos
  const DEBOUNCE_DELAY = 1500;

  // Efeito para debounce do código do cliente
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCodCliente(codCliente);
    }, DEBOUNCE_DELAY);
    return () => clearTimeout(timer);
  }, [codCliente]);

  // Efeito para debounce da razão social
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedRazaoSocial(razaoSocial);
    }, DEBOUNCE_DELAY);
    return () => clearTimeout(timer);
  }, [razaoSocial]);

  // Efeito para debounce da data inicial
  useEffect(() => {
    if (!dataInicial || isValidDate(dataInicial)) {
      const timer = setTimeout(() => {
        setDebouncedDataInicial(dataInicial);
      }, DEBOUNCE_DELAY);
      return () => clearTimeout(timer);
    }
  }, [dataInicial]);

  // Efeito para debounce da data final
  useEffect(() => {
    if (!dataFinal || isValidDate(dataFinal)) {
      const timer = setTimeout(() => {
        setDebouncedDataFinal(dataFinal);
      }, DEBOUNCE_DELAY);
      return () => clearTimeout(timer);
    }
  }, [dataFinal]);

  // Efeito para buscar dados do usuário (empresa e rota)
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;
        if (!session?.user) throw new Error('Usuário não autenticado.');

        const { data: userDetails, error: userError } = await supabase
          .from('usuarios')
          .select('empresa, codusur') // empresa aqui é o ID
          .eq('id', session.user.id)
          .single();

        if (userError) throw userError;
        if (!userDetails?.empresa) throw new Error('ID da Empresa não encontrado para o usuário.');

        const { data: empresaDetails, error: empresaError } = await supabase
          .from('empresa') // Tabela que relaciona ID da empresa com a ROTA (string)
          .select('rota')
          .eq('id', userDetails.empresa) // userDetails.empresa é o ID numérico
          .single();

        if (empresaError) throw empresaError;
        if (!empresaDetails?.rota) throw new Error('Rota da API não encontrada para a empresa.');
        
        setUserData({
          ...userDetails,
          empresa: userDetails.empresa, // ID numérico da empresa
          empresaRota: empresaDetails.rota, // String da rota, ex: "UNIFRIOS"
        });

      } catch (err: any) {
        console.error('Erro ao buscar dados do usuário:', err);
        // Alert.alert('Erro', 'Não foi possível buscar os dados do usuário para sincronização.');
      }
    };
    fetchUserData();
  }, []);

  const handleRefreshAndSync = async () => {
    if (!userData || !userData.empresa || !userData.empresaRota) {
      Alert.alert("Erro", "Dados do usuário incompletos. Não é possível sincronizar.");
      return;
    }

    setLoadingSync(true);

    try {
      // Usa a instância do Supabase configurada
      console.log('Iniciando sincronização com o Supabase...');
      
      // Primeira chamada: FINANCEIROListaClienteDeb
      const { data: resultadoClientes, error: erroClientes } = await supabase.rpc(
        'http_obter_json_api_async',
        {
          caminhoapi: 'FINANCEIROListaClienteDeb',
          functionproxima: 'listasconsultaapi',
          idempresa: String(userData.empresa),
          rotaapi: userData.empresaRota,
        }
      );
      
      if (erroClientes) {
        console.error('Erro na primeira chamada RPC:', erroClientes);
        throw new Error(`Erro na sincronização (Clientes): ${erroClientes.message}`);
      }
      
      console.log('Sucesso - FINANCEIROListaClienteDeb:', resultadoClientes);
      
      // Analisando a estrutura dos dados para encontrar campos de telefone
      if (resultadoClientes && resultadoClientes.length > 0) {
        console.log('Exemplo de cliente recebido:', resultadoClientes[0]);
        console.log('Campos disponíveis no cliente:', Object.keys(resultadoClientes[0]));
        
        // Verificando campos específicos de telefone
        const camposTelefone = ['S_TELEFONE', 'S_TELCOB', 'S_TELCLI', 'S_FONE', 'S_CELULAR'];
        camposTelefone.forEach(campo => {
          console.log(`Campo ${campo} presente:`, resultadoClientes[0][campo] ? 'SIM' : 'NÃO');
          if (resultadoClientes[0][campo]) {
            console.log(`Valor do campo ${campo}:`, resultadoClientes[0][campo]);
          }
        });
      }
      
      // Segunda chamada: FINANCEIROListaTituloDeb
      const { data: resultadoTitulos, error: erroTitulos } = await supabase.rpc(
        'http_obter_json_api_async',
        {
          caminhoapi: 'FINANCEIROListaTituloDeb',
          functionproxima: 'listasconsultaapi',
          idempresa: String(userData.empresa),
          rotaapi: userData.empresaRota,
        }
      );

      if (erroTitulos) {
        console.error('Erro na segunda chamada RPC:', erroTitulos);
        throw new Error(`Erro na sincronização (Títulos): ${erroTitulos.message}`);
      }
      
      console.log('Sucesso - FINANCEIROListaTituloDeb:', resultadoTitulos);

      setSnackbar({
        visible: true,
        mensagem: 'Sincronização com a API concluída.',
        tipo: 'success'
      });
      
      // Refetch para atualizar a lista de clientes na UI
      refetch();
    } catch (err: any) {
      console.error('Falha na sincronização RPC:', err);
      setSnackbar({
        visible: true,
        mensagem: `Falha na sincronização: ${err.message || 'Não foi possível sincronizar com a API.'}`,
        tipo: 'error'
      });
    } finally {
      setLoadingSync(false);
    }
  };

  // Função para formatar valores monetários
  const formatCurrency = useCallback((value: string) => {
    if (!value) return 'R$ 0,00';
    
    // Converte para número e formata
    const num = parseFloat(value);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(num);
  }, []);

  // OPÇÃO 4: Input livre + DDI editável + Validação visual
  const [telefoneOpcao4, setTelefoneOpcao4] = useState('');
  const [timeoutFormatacao, setTimeoutFormatacao] = useState<NodeJS.Timeout | null>(null);

  const handleTelefoneOpcao4Change = (texto: string) => {
    // Atualiza o valor
    setTelefoneOpcao4(texto);
    
    // Limpa o timeout anterior
    if (timeoutFormatacao) {
      clearTimeout(timeoutFormatacao);
    }
    
    // Define novo timeout para formatar após 1.5 segundos de inatividade
    const novoTimeout = setTimeout(() => {
      formatarTelefoneOpcao4AoSair();
    }, 1500);
    
    setTimeoutFormatacao(novoTimeout);
  };

  const formatarTelefoneOpcao4AoSair = () => {
    // SEMPRE limpa TUDO primeiro - remove parênteses, espaços, hífens, etc
    const numeros = telefoneOpcao4.replace(/\D/g, '');
    
    // Não formata se estiver vazio ou muito curto (menos de 10 dígitos = DDD + número)
    if (numeros.length < 10) {
      // Se tiver entre 8 e 9 dígitos, deixa sem formatar (número incompleto)
      return;
    }
    
    // Remove qualquer DDI que possa ter vindo colado
    let semPrefixo = numeros;
    
    // Se começar com 55 e tiver mais de 11 dígitos, remove o 55
    if (numeros.startsWith('55') && numeros.length >= 12) {
      semPrefixo = numeros.slice(2);
    } 
    // Se começar com 1 e tiver 11+ dígitos, pode ser DDI 1 (EUA)
    else if (numeros.startsWith('1') && numeros.length >= 11 && ddiOpcao4 !== '1') {
      semPrefixo = numeros.slice(1);
    }
    
    // Formata o número com base no tamanho
    let formatado = '';
    
    if (semPrefixo.length === 10) {
      // 10 dígitos: DDD (2) + Número (8) - telefone fixo
      formatado = semPrefixo.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    } else if (semPrefixo.length === 11) {
      // 11 dígitos: DDD (2) + Número (9) - celular
      formatado = semPrefixo.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (semPrefixo.length > 11) {
      // Mais de 11 dígitos: pega os últimos 11 (assume celular)
      const ultimos11 = semPrefixo.slice(-11);
      formatado = ultimos11.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else {
      // Menos de 10 dígitos: não formata
      return;
    }
    
    // Atualiza apenas se formatou algo válido
    if (formatado) {
      setTelefoneOpcao4(formatado);
    }
  };

  const getTelefoneCompletoOpcao4 = () => {
    const numeros = telefoneOpcao4.replace(/\D/g, '');
    return `${ddiOpcao4}${numeros}`;
  };

  const isValidTelefoneOpcao4 = () => {
    const numeros = telefoneOpcao4.replace(/\D/g, '');
    // Válido se tiver DDI preenchido + DDD (2 dígitos) + número (8 ou 9 dígitos)
    return ddiOpcao4.length >= 1 && numeros.length >= 10 && numeros.length <= 11;
  };

  // Função para buscar os títulos do cliente
  const buscarTitulosCliente = useCallback(async (cliente: Cliente) => {
    if (!userData?.empresa) return [];
    
    try {
      setCarregandoTitulos(true);
      
      // Busca os títulos do cliente na tabela listasconsultaapi
      const { data, error } = await supabase
        .from('listasconsultaapi')
        .select('data')
        .eq('rotaapi', 'FINANCEIROListaTituloDeb')
        .eq('codempresa', userData.empresa)
        .order('created_at', { ascending: false })
        .limit(1);
        
      if (error) throw error;
      if (!data || data.length === 0) return [];
      
      // Filtra os títulos do cliente específico
      const titulos = data[0].data.filter((titulo: any) => 
        String(titulo.I_CODCLI) === String(cliente.I_CODCLI)
      );
      
      // Ordena por data de vencimento
      return titulos.sort((a: any, b: any) => {
        // Comparação direta de strings ISO (YYYY-MM-DD) é segura para ordenação
        // e evita problemas de fuso horário
        if (a.D_DTVENC && b.D_DTVENC) {
          return a.D_DTVENC.localeCompare(b.D_DTVENC);
        }
        return 0;
      });
    } catch (error) {
      console.error('Erro ao buscar títulos:', error);
      return [];
    } finally {
      setCarregandoTitulos(false);
    }
  }, [userData]);
  
  // Função para preparar e enviar mensagem de cobrança via WhatsApp
  const enviarMensagemCobranca = useCallback(async (cliente: Cliente) => {
    try {
      console.log('Cliente selecionado para cobrança:', cliente);
      console.log('Propriedades disponíveis no cliente:', Object.keys(cliente));
      setClienteSelecionado(cliente);
      
      // Busca os títulos do cliente
      const titulos = await buscarTitulosCliente(cliente);
      setTitulosCliente(titulos);
      
      // Verifica se o cliente tem o telefone já formatado
      console.log('Verificando telefone do cliente...');
      console.log('S_TELCOBFORMATADO:', cliente.S_TELCOBFORMATADO);
      
      // Usa o telefone já formatado se disponível
      if (cliente.S_TELCOBFORMATADO && cliente.S_TELCOBFORMATADO.length > 5) {
        console.log('Usando telefone formatado do cliente:', cliente.S_TELCOBFORMATADO);
        const telefoneCompleto = cliente.S_TELCOBFORMATADO;
        setTelefoneEnvio(telefoneCompleto);
        
        // Para Opção 4: Separar DDI do número
        const numeros = telefoneCompleto.replace(/\D/g, '');
        if (numeros.length >= 12) {
          // Tem DDI + número completo
          const ddi = numeros.slice(0, 2);
          const numero = numeros.slice(2);
          setDdiOpcao4(ddi);
          
          // Formata o número
          let numeroFormatado = '';
          if (numero.length <= 10) {
            numeroFormatado = numero.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
          } else {
            numeroFormatado = numero.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
          }
          setTelefoneOpcao4(numeroFormatado);
        }
      } else {
        // Caso não tenha o telefone formatado, tenta outros campos
        console.log('Telefone formatado não encontrado, verificando outros campos...');
        console.log('S_TELCOB:', cliente.S_TELCOB);
        
        // Usa o campo S_TELCOB como backup
        let telefoneCliente = cliente.S_TELCOB || '';
        console.log('Telefone encontrado (S_TELCOB):', telefoneCliente);
        
        // Remove caracteres não numéricos do telefone
        telefoneCliente = telefoneCliente.replace(/\D/g, '');
        
        // Se o telefone não começar com 55 (Brasil), adiciona
        if (telefoneCliente && !telefoneCliente.startsWith('55')) {
          telefoneCliente = `55${telefoneCliente}`;
        }
        
        // Define um telefone padrão se não encontrar nenhum válido
        const telefoneEnviar = (telefoneCliente && telefoneCliente.length > 5) ? telefoneCliente : '55829';
        console.log('Telefone definido para envio:', telefoneEnviar);
        setTelefoneEnvio(telefoneEnviar);
        
        // Para Opção 4: Separar DDI do número
        const numeros = telefoneEnviar.replace(/\D/g, '');
        if (numeros.length >= 12) {
          const ddi = numeros.slice(0, 2);
          const numero = numeros.slice(2);
          setDdiOpcao4(ddi);
          
          let numeroFormatado = '';
          if (numero.length <= 10) {
            numeroFormatado = numero.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
          } else {
            numeroFormatado = numero.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
          }
          setTelefoneOpcao4(numeroFormatado);
        }
      }
      
      // Abre o diálogo de confirmação
      setDialogVisible(true);
    } catch (error: any) {
      console.error('Erro ao preparar envio de mensagem:', error);
      setSnackbar({
        visible: true,
        mensagem: `Erro ao preparar envio de mensagem: ${error.message}`,
        tipo: 'error'
      });
    }
  }, [buscarTitulosCliente]);

  // A função formatarData foi removida e substituída pela função formatDate importada

  // Função para confirmar e enviar a mensagem
  const confirmarEnvioMensagem = async () => {
    if (!clienteSelecionado) return;
    try {
      // CAPTURA O TELEFONE ANTES DE FECHAR O DIALOG!
      const telefone = getTelefoneCompletoOpcao4();
      
      console.log('🔥 TELEFONE CAPTURADO:', telefone);
      console.log('📞 DDI:', ddiOpcao4);
      console.log('📞 Número:', telefoneOpcao4);
      
      // Verifica se o código da empresa está disponível
      if (!userData || !userData.empresa) {
        throw new Error('Código da empresa não disponível');
      }
      
      console.log('Código da empresa disponível:', userData.empresa);
      
      // Verificar se o número é um WhatsApp válido
      console.log('Verificando se o número é um WhatsApp válido:', telefone);
      const numeroValido = await whatsappService.verificarNumeroWhatsApp(telefone, userData.empresa);
      
      if (!numeroValido) {
        setSnackbar({
          visible: true,
          mensagem: 'O número informado não é um WhatsApp válido. Verifique e tente novamente.',
          tipo: 'error'
        });
        return;
      }
      
      // Fecha o diálogo e inicia o envio
      setEnviandoMensagem(true);
      setDialogVisible(false);
      
      console.log('🔥 TELEFONE APÓS FECHAR DIALOG:', telefone);
      
      console.log('Número de WhatsApp válido, prosseguindo com o envio...');
      
      // Busca o modelo de mensagem no banco de dados com o código da empresa
      const modeloMensagem = await whatsappService.buscarModelo('cobranca', userData.empresa);
      
      if (!modeloMensagem) {
        throw new Error('Modelo de mensagem não encontrado no banco de dados');
      }
      
      // Prepara os valores formatados
      const valorFormatado = formatCurrency(clienteSelecionado.F_VALORDEB || '0');
      
      // Substitui as variáveis no modelo
      let mensagem = modeloMensagem;
      
      // Buscar dados da empresa
      const { data: empresaData, error: empresaError } = await supabase
        .from('empresa')
        .select('*')
        .eq('id', userData.empresa)
        .single();
        
      if (empresaError) {
        console.error('Erro ao buscar dados da empresa:', empresaError);
        throw new Error('Erro ao buscar dados da empresa');
      }
      
      // Log para debug dos dados da empresa e cliente
      console.log('Dados da empresa:', empresaData);
      console.log('Campos disponíveis na empresa:', Object.keys(empresaData || {}));
      console.log('Dados do cliente selecionado:', {
        S_CLIENTE: clienteSelecionado.S_CLIENTE,
        S_CGC: clienteSelecionado.S_CGC,
        S_CGCENT: clienteSelecionado.S_CGCENT
      });
      
      // Formata os detalhes dos títulos para incluir na mensagem
      let detalheTitulos = '';
      
      if (titulosCliente && titulosCliente.length > 0) {
        detalheTitulos = '\n\n📄 *TÍTULOS EM ABERTO:*\n';
        titulosCliente.forEach((titulo, index) => {
          // Corrigindo o problema de fuso horário na formatação da data
          let dataFormatada = '-';
          
          // Formato esperado: YYYY-MM-DD (ISO)
          if (titulo.D_DTVENC && titulo.D_DTVENC.match(/^\d{4}-\d{2}-\d{2}$/)) {
            // Dividir a data em partes
            const [year, month, day] = titulo.D_DTVENC.split('-').map(Number);
            // Formatar diretamente sem criar objeto Date para evitar problemas de timezone
            dataFormatada = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
          } else if (titulo.D_DTVENC) {
            // Fallback para o método anterior caso o formato seja diferente
            const dataVenc = new Date(titulo.D_DTVENC);
            dataFormatada = `${String(dataVenc.getDate()).padStart(2, '0')}/${String(dataVenc.getMonth() + 1).padStart(2, '0')}/${dataVenc.getFullYear()}`;
          }
          
          const valorTitulo = formatCurrency(titulo.F_VALOR || '0');
          
          detalheTitulos += `\n${index + 1}. Título: ${titulo.S_DUPLICPREST || '-'}`;
          detalheTitulos += `\n   Vencimento: ${dataFormatada}`;
          detalheTitulos += `\n   Valor: ${valorTitulo}`;
          
          if (titulo.S_LINHADIGFORMATADA) {
            detalheTitulos += `\n   Linha Digitável: ${titulo.S_LINHADIGFORMATADA}`;
          }
          
          detalheTitulos += '\n';
        });
      } else {
        detalheTitulos = '\n\nNão foram encontrados detalhes dos títulos.';
      }
      
      // Mapeamento de variáveis do modelo para os dados do cliente
      const substituicoes: Record<string, string> = {
        '[cliente]': clienteSelecionado.S_CLIENTE || '',
        '[codcli]': String(clienteSelecionado.I_CODCLI || ''),
        '[cgc]': clienteSelecionado.S_CGCENT || '', // Corrigido: usar S_CGCENT em vez de S_CGC
        '[valordeb]': valorFormatado,
        '[qtdetitul]': String(clienteSelecionado.I_QTDETITULOS || 0),
        '[mindtvenc]': formatDate(clienteSelecionado.D_MINDTVENC || ''),
        '[maxdtvenc]': formatDate(clienteSelecionado.D_MAXDTVENC || ''),
        '[titulos]': detalheTitulos,
        '[empresanome]': empresaData?.nome || '',
        '[empresatel]': empresaData?.telefone || '',
        '[empresacnpj]': empresaData?.dados_empresa?.cnpj || empresaData?.cnpj || ''
      };
      
      console.log('Datas formatadas:', {
        mindtvenc: substituicoes['[mindtvenc]'],
        maxdtvenc: substituicoes['[maxdtvenc]']
      });
      
      // Aplica todas as substituições de forma limpa
      Object.entries(substituicoes).forEach(([chave, valor]) => {
        // Escape caracteres especiais em regex
        const chaveEscapada = chave.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        mensagem = mensagem.replace(new RegExp(chaveEscapada, 'g'), valor);
      });
      
      // USA O TELEFONE JÁ CAPTURADO (não busca de novo!)
      console.log('🚀 ENVIANDO PARA:', telefone);
      
      // Envia a mensagem e registra a cobrança
      await whatsappService.enviarTexto({ 
        telefone: telefone, 
        mensagem,
        // Passa o codempresa para o serviço
        codempresa: userData?.empresa,
        options: {
          delay: 1200
        },
        // Registra a cobrança na API
        registrarCobranca: true,
        dadosCobranca: {
          codfunc: userData?.codusur || '',
          codcli: String(clienteSelecionado.I_CODCLI || ''),
          titulos: titulosCliente // Passa todos os títulos do cliente
        }
      });
      
      setSnackbar({ visible: true, mensagem: 'Mensagem enviada com sucesso!', tipo: 'success' });
      
      // Atualiza os dados após o envio da cobrança
      console.log('Atualizando dados após cobrança...');
      await handleRefreshAndSync();
    } catch (error: any) {
      console.error('Erro ao enviar mensagem:', error);
      setSnackbar({ visible: true, mensagem: `Erro ao enviar mensagem: ${error.message}`, tipo: 'error' });
    } finally {
      setEnviandoMensagem(false);
    }
  };

  // Busca clientes usando o hook personalizado
  const { clientes, loading, error, refetch } = useListaClientes({
    codCliente: debouncedCodCliente,
    razaoSocial: debouncedRazaoSocial,
    dataInicial: debouncedDataInicial,
    dataFinal: debouncedDataFinal,
  });

  const totalClientes = clientes?.length || 0;
  const totalValor = clientes?.reduce((sum, c) => sum + parseFloat(c.F_VALORDEB || '0'), 0) || 0;
  const filtrosAtivos = [codCliente, razaoSocial, dataInicial, dataFinal].filter(Boolean).length;

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <Text style={styles.headerTitle}>Cobranças</Text>
          <View style={styles.headerBadges}>
            <View style={styles.badge}>
              <Text style={styles.badgeText}>{totalClientes} clientes</Text>
            </View>
            <View style={[styles.badge, styles.badgeMoney]}>
              <Text style={styles.badgeText}>{formatCurrency(totalValor.toString())}</Text>
            </View>
          </View>
        </View>
        <View style={styles.headerActions}>
          <View style={styles.filterButtonContainer}>
            <IconButton
              icon="filter"
              size={24}
              iconColor={filtrosAtivos > 0 ? theme.colors.primary : '#666'}
              onPress={() => setShowFilters(!showFilters)}
              style={filtrosAtivos > 0 ? styles.filterActive : undefined}
            />
            {filtrosAtivos > 0 && (
              <View style={styles.filterBadge}>
                <Text style={styles.filterBadgeText}>{filtrosAtivos}</Text>
              </View>
            )}
          </View>
          <IconButton
            icon="refresh"
            size={24}
            onPress={handleRefreshAndSync}
            disabled={loadingSync}
          />
          {loadingSync && (
            <ActivityIndicator size="small" color={theme.colors.primary} style={{ marginLeft: 8 }} />
          )}
        </View>
      </View>

      {showFilters && (
        <View style={styles.searchContainer}>
          <TextInput
            label="Código do Cliente"
            value={codCliente}
            onChangeText={setCodCliente}
            mode="outlined"
            style={styles.input}
            keyboardType="numeric"
          />
          <TextInput
            label="Razão Social"
            value={razaoSocial}
            onChangeText={setRazaoSocial}
            mode="outlined"
            style={styles.input}
          />
          <TextInput
            label="Data Inicial"
            value={dataInicial}
            onChangeText={(text) => setDataInicial(formatDate(text))}
            mode="outlined"
            style={styles.input}
            keyboardType="numeric"
            placeholder="DD/MM/AAAA"
            maxLength={10}
          />
          <TextInput
            label="Data Final"
            value={dataFinal}
            onChangeText={(text) => setDataFinal(formatDate(text))}
            mode="outlined"
            style={styles.input}
            keyboardType="numeric"
            placeholder="DD/MM/AAAA"
            maxLength={10}
          />
        </View>
      )}

      <ScrollView contentContainerStyle={styles.scrollContent}>
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
          </View>
        ) : error ? (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>Erro ao carregar clientes: {error}</Text>
          </View>
        ) : clientes?.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text>Nenhum cliente encontrado</Text>
          </View>
        ) : (
          clientes?.map((cliente) => (
            <ClienteCard
              key={cliente.I_CODCLI}
              cliente={cliente}
              formatCurrency={formatCurrency}
              onPress={() => navigation.navigate('ListaTitulos', { cliente })}
              onRobotPress={enviarMensagemCobranca}
            />
          ))
        )}
      </ScrollView>

      {/* Diálogo de confirmação */}
      <Portal>
        <Dialog visible={dialogVisible} onDismiss={() => setDialogVisible(false)} style={styles.dialog}>
          <Dialog.Title style={styles.dialogTitle}>
            <IconButton icon="whatsapp" size={24} iconColor="#25D366" style={styles.dialogTitleIcon} />
            Enviar Cobrança via WhatsApp
          </Dialog.Title>
          <Dialog.Content style={styles.dialogContentWrapper}>
            {/* Card de Informações do Cliente - Compacto */}
            <Surface style={styles.dialogCard} elevation={1}>
              <View style={styles.dialogCardHeader}>
                <IconButton icon="account" size={20} iconColor={theme.colors.primary} style={styles.cardIcon} />
                <Text style={styles.dialogCardTitle}>Informações do Cliente</Text>
              </View>
              <Text style={styles.dialogClienteNome}>{clienteSelecionado?.S_CLIENTE}</Text>
              <View style={styles.clienteInfoGrid}>
                <View style={styles.clienteInfoItem}>
                  <IconButton icon="cash" size={16} iconColor="#F44336" style={styles.infoIconCompact} />
                  <View>
                    <Text style={styles.dialogInfoLabelCompact}>Valor Total</Text>
                    <Text style={styles.dialogInfoValueCompact}>{clienteSelecionado ? formatCurrency(clienteSelecionado.F_VALORDEB || '0') : ''}</Text>
                  </View>
                </View>
                <View style={styles.clienteInfoItem}>
                  <IconButton icon="file-document-multiple" size={16} iconColor="#FF9800" style={styles.infoIconCompact} />
                  <View>
                    <Text style={styles.dialogInfoLabelCompact}>Qtde. Títulos</Text>
                    <Text style={styles.dialogInfoValueCompact}>{clienteSelecionado?.I_QTDETITULOS || 0}</Text>
                  </View>
                </View>
              </View>
            </Surface>

            {/* Card de Títulos com ScrollView Próprio */}
            {carregandoTitulos ? (
              <ActivityIndicator size="small" style={styles.dialogLoading} />
            ) : titulosCliente.length > 0 ? (
              <Surface style={styles.dialogCard} elevation={1}>
                <View style={styles.dialogCardHeader}>
                  <IconButton icon="file-document" size={20} iconColor={theme.colors.primary} style={styles.cardIcon} />
                  <Text style={styles.dialogCardTitle}>Títulos em Aberto ({titulosCliente.length})</Text>
                </View>
                <ScrollView style={styles.titulosScrollView} nestedScrollEnabled={true}>
                  {titulosCliente.map((titulo, index) => (
                    <Surface key={index} style={styles.tituloCard} elevation={0}>
                      <View style={styles.tituloCardHeader}>
                        <Text style={styles.tituloNumero}>#{index + 1}</Text>
                        <Text style={styles.tituloDuplicata}>{titulo.S_DUPLICPREST}</Text>
                      </View>
                      <View style={styles.tituloCardBody}>
                        <View style={styles.tituloInfoRow}>
                          <IconButton icon="calendar" size={14} iconColor="#666" style={styles.tituloIcon} />
                          <Text style={styles.tituloLabel}>Vencimento:</Text>
                          <Text style={styles.tituloValue}>{formatDate(titulo.D_DTVENC)}</Text>
                        </View>
                        <View style={styles.tituloInfoRow}>
                          <IconButton icon="currency-usd" size={14} iconColor="#666" style={styles.tituloIcon} />
                          <Text style={styles.tituloLabel}>Valor:</Text>
                          <Text style={styles.tituloValueMoney}>{formatCurrency(titulo.F_VALOR)}</Text>
                        </View>
                      </View>
                    </Surface>
                  ))}
                </ScrollView>
              </Surface>
            ) : (
              <Surface style={styles.dialogCard} elevation={1}>
                <Text style={styles.dialogInfoEmpty}>Nenhum título encontrado para este cliente.</Text>
              </Surface>
            )}

            {/* Card de Telefone para Envio */}
            <Surface style={[styles.dialogCard, styles.cardTesteDestaque]} elevation={2}>
              <View style={styles.dialogCardHeader}>
                <IconButton icon="whatsapp" size={20} iconColor="#25D366" style={styles.cardIcon} />
                <Text style={styles.dialogCardTitle}>Telefone para Envio</Text>
              </View>
              
              <View style={styles.telefoneRow}>
                <View style={styles.ddiContainer}>
                  <Text style={styles.ddiLabel}>DDI (País)</Text>
                  <TextInput
                    value={ddiOpcao4}
                    onChangeText={(text) => setDdiOpcao4(text.replace(/\D/g, '').slice(0, 3))}
                    mode="outlined"
                    style={[styles.ddiInput, !ddiOpcao4 && styles.ddiInputVazio]}
                    keyboardType="number-pad"
                    placeholder="55"
                    placeholderTextColor="#ccc"
                    maxLength={3}
                    error={!ddiOpcao4}
                  />
                  <Text style={styles.ddiExemplo}>Ex: 55</Text>
                </View>
                
                <View style={styles.telefoneContainer}>
                  <Text style={styles.telefoneLabel}>DDD + Número</Text>
                  <View style={styles.telefoneInputRow}>
                    <TextInput
                      value={telefoneOpcao4}
                      onChangeText={handleTelefoneOpcao4Change}
                      onBlur={formatarTelefoneOpcao4AoSair}
                      mode="outlined"
                      style={styles.telefoneInputComPrefixo}
                      keyboardType="default"
                      placeholder="Digite ou cole o telefone"
                      placeholderTextColor="#999"
                      left={<TextInput.Icon icon="phone" color="#25D366" />}
                      right={
                        telefoneOpcao4 ? (
                          <TextInput.Icon 
                            icon={isValidTelefoneOpcao4() ? "check-circle" : "alert-circle"} 
                            color={isValidTelefoneOpcao4() ? "#4CAF50" : "#F44336"} 
                          />
                        ) : null
                      }
                    />
                    <IconButton
                      icon="auto-fix"
                      size={20}
                      iconColor="#25D366"
                      style={styles.formatButton}
                      onPress={formatarTelefoneOpcao4AoSair}
                    />
                  </View>
                  <Text style={styles.telefoneExemplo}>Digite ou cole. Clique em ✨ ou saia do campo para formatar.</Text>
                </View>
              </View>
              
              {!ddiOpcao4 && (
                <Text style={styles.ddiAlerta}>⚠️ Informe o código DDI do país (55 = Brasil)</Text>
              )}
              
              <Text style={[styles.telefonePreview, isValidTelefoneOpcao4() && styles.telefonePreviewValido]}>
                {isValidTelefoneOpcao4() ? '✅' : '⚠️'} Número completo: {getTelefoneCompletoOpcao4() || `${ddiOpcao4 || 'DDI'}...`}
              </Text>
            </Surface>
          </Dialog.Content>
          <Dialog.Actions style={styles.dialogActions}>
            <Button onPress={() => setDialogVisible(false)} textColor={theme.colors.error}>Cancelar</Button>
            <Button 
              mode="contained" 
              onPress={confirmarEnvioMensagem} 
              loading={enviandoMensagem}
              disabled={enviandoMensagem || !isValidTelefoneOpcao4()}
              icon="send"
              buttonColor="#25D366"
            >
              Enviar
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      {/* Snackbar para feedback */}
      <Portal>
        <Snackbar
          visible={snackbar.visible}
          onDismiss={() => setSnackbar(prev => ({ ...prev, visible: false }))}
          duration={3000}
          style={{
            backgroundColor: 
              snackbar.tipo === 'success' ? '#4CAF50' : 
              snackbar.tipo === 'error' ? '#F44336' :
              '#2196F3'
          }}
        >
          {snackbar.mensagem}
        </Snackbar>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  dialog: {
    maxHeight: '90%',
    backgroundColor: '#f5f5f5',
  },
  dialogTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 18,
    fontWeight: 'bold',
    backgroundColor: '#f5f5f5',
  },
  dialogTitleIcon: {
    margin: 0,
    marginRight: -8,
  },
  dialogContentWrapper: {
    paddingHorizontal: 0,
    paddingVertical: 8,
    backgroundColor: '#f5f5f5',
  },
  dialogCard: {
    marginHorizontal: 24,
    marginVertical: 8,
    padding: 16,
    borderRadius: 12,
    backgroundColor: '#fff',
  },
  dialogCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  cardIcon: {
    margin: 0,
    marginRight: -4,
  },
  dialogCardTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  dialogClienteNome: {
    fontSize: 17,
    fontWeight: 'bold',
    marginBottom: 12,
    color: '#000',
  },
  clienteInfoGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  clienteInfoItem: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
    padding: 8,
    borderRadius: 8,
  },
  infoIconCompact: {
    margin: 0,
    marginRight: 4,
  },
  dialogInfoLabelCompact: {
    fontSize: 11,
    color: '#666',
    marginBottom: 2,
  },
  dialogInfoValueCompact: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#000',
  },
  dialogInfoEmpty: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  dialogLoading: {
    marginVertical: 24,
  },
  titulosScrollView: {
    maxHeight: 200,
  },
  tituloCard: {
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
    borderLeftWidth: 3,
    borderLeftColor: '#FF9800',
  },
  tituloCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  tituloNumero: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#fff',
    backgroundColor: '#FF9800',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    marginRight: 8,
  },
  tituloDuplicata: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#000',
  },
  tituloCardBody: {
    marginTop: 4,
  },
  tituloInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  tituloIcon: {
    margin: 0,
    marginRight: -8,
  },
  tituloLabel: {
    fontSize: 13,
    color: '#666',
    marginRight: 4,
  },
  tituloValue: {
    fontSize: 13,
    color: '#333',
  },
  tituloValueMoney: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#F44336',
  },
  maisInfo: {
    fontSize: 13,
    color: '#666',
    textAlign: 'center',
    marginTop: 8,
    fontStyle: 'italic',
  },
  telefoneInput: {
    backgroundColor: '#fff',
    marginBottom: 8,
  },
  telefoneHelp: {
    fontSize: 12,
    color: '#666',
    lineHeight: 18,
  },
  cardTeste: {
    borderLeftWidth: 4,
    borderLeftColor: '#2196F3',
  },
  prefixoFixo: {
    backgroundColor: '#e3f2fd',
    paddingHorizontal: 16,
    paddingVertical: 18,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#2196F3',
  },
  prefixoEditavel: {
    backgroundColor: '#e8f5e9',
    paddingHorizontal: 12,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#4CAF50',
    flexDirection: 'row',
    alignItems: 'center',
  },
  prefixoTexto: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  prefixoIcon: {
    margin: 0,
    marginLeft: -8,
  },
  telefoneInputComPrefixo: {
    flex: 1,
    backgroundColor: '#fff',
  },
  telefonePreview: {
    fontSize: 13,
    color: '#4CAF50',
    fontWeight: 'bold',
    marginBottom: 4,
    backgroundColor: '#f1f8f4',
    padding: 8,
    borderRadius: 6,
  },
  telefonePreviewValido: {
    backgroundColor: '#e8f5e9',
    borderWidth: 1,
    borderColor: '#4CAF50',
  },
  telefoneRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 8,
  },
  ddiContainer: {
    minWidth: 80,
    maxWidth: 120,
    flex: 0,
  },
  ddiLabel: {
    fontSize: 11,
    color: '#666',
    marginBottom: 4,
    fontWeight: 'bold',
  },
  ddiInput: {
    backgroundColor: '#fff',
    textAlign: 'center',
    fontSize: 18,
    fontWeight: 'bold',
    height: 56,
  },
  ddiInputVazio: {
    borderColor: '#F44336',
    borderWidth: 2,
  },
  ddiExemplo: {
    fontSize: 8,
    color: '#999',
    marginTop: 1,
    textAlign: 'center',
  },
  telefoneContainer: {
    flex: 1,
    minWidth: 200,
    maxWidth: 400,
  },
  telefoneLabel: {
    fontSize: 11,
    color: '#666',
    marginBottom: 4,
    fontWeight: 'bold',
  },
  telefoneInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  telefoneExemplo: {
    fontSize: 10,
    color: '#999',
    marginTop: 2,
  },
  formatButton: {
    margin: 0,
    backgroundColor: '#e8f5e9',
    borderRadius: 8,
  },
  ddiAlerta: {
    fontSize: 12,
    color: '#F44336',
    marginTop: 4,
    marginBottom: 8,
    fontWeight: 'bold',
  },
  cardTesteDestaque: {
    borderLeftWidth: 4,
    borderLeftColor: '#25D366',
    backgroundColor: '#fafafa',
  },
  dialogActions: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#f5f5f5',
  },
  header: {
    backgroundColor: '#fff',
    padding: 16,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
  },
  headerBadges: {
    flexDirection: 'row',
    gap: 8,
  },
  badge: {
    backgroundColor: '#e3f2fd',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  badgeMoney: {
    backgroundColor: '#e8f5e9',
  },
  badgeText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#333',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  filterButtonContainer: {
    position: 'relative',
  },
  filterActive: {
    backgroundColor: '#e3f2fd',
  },
  filterBadge: {
    position: 'absolute',
    top: 4,
    right: 4,
    backgroundColor: '#2196F3',
    borderRadius: 10,
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterBadgeText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  searchContainer: {
    padding: 16,
    backgroundColor: '#fff',
    gap: 12,
  },
  input: {
    backgroundColor: '#fff',
  },
  dateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  dateInput: {
    flex: 1,
  },
  dateText: {
    marginHorizontal: 4,
  },
  clientList: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorContainer: {
    padding: 20,
    alignItems: 'center',
  },
  errorText: {
    color: '#dc3545',
    textAlign: 'center',
  },
  emptyContainer: {
    padding: 20,
    alignItems: 'center',
  },
  card: {
    marginBottom: 16,
    borderRadius: 12,
    borderLeftWidth: 4,
    backgroundColor: '#fff',
  },
  cardContent: {
    padding: 16,
  },
  cardHeader: {
    marginBottom: 12,
  },
  cardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    minWidth: 50,
    alignItems: 'center',
  },
  statusBadgeText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  clientCode: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#666',
    marginBottom: 4,
  },
  clientName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  cardInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  infoItem: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
    padding: 8,
    borderRadius: 8,
  },
  infoIcon: {
    margin: 0,
    marginRight: -4,
  },
  infoLabel: {
    fontSize: 10,
    color: '#666',
    marginBottom: 2,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#333',
  },
  infoValueMoney: {
    color: '#4CAF50',
  },
  robotButton: {
    position: 'absolute',
    top: 8,
    right: 8,
  },
  scrollContent: {
    flexGrow: 1,
  },
});
